import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import { useNavigate } from 'react-router-dom';
import { userEmail, userRole, cmpAccess } from 'src/store/login';
import { getCookie } from 'src/utils/cookies';
import { getDomainFromEmail, hasSettingsPermission } from 'src/utils/utilityMethods';
import { SPROUTS_ADMIN_DOMAIN } from 'src/constants/constants';
import { setActiveChampionsTab } from 'src/store/champion-tracking/championImport.actions';
import { trackDatabaseSearchTabs } from 'src/mix-panel/database-search/functions';
import { trackContactList } from 'src/mix-panel/lists/functions';
import { setActiveListTab } from 'src/store/dashboard/tp-list-page.actions';
import { useProfile } from 'src/hooks/useProfile';
import { NavBarGroupType } from './types';
import DBSIcon from 'src/assets/navbar-icons/dbs-icon.svg';
import DBSIconActive from 'src/assets/navbar-icons/dbs-icon-active.svg';
import ListIcon from 'src/assets/navbar-icons/list-icon.svg';
import ListIconActive from 'src/assets/navbar-icons/list-icon-active.svg';
import TALIcon from 'src/assets/navbar-icons/tal-icon.svg';
import TALIconActive from 'src/assets/navbar-icons/tal-icon-active.svg';
import RunCampaignIcon from 'src/assets/navbar-icons/campaign-icon.svg';
import RunCampaignIconActive from 'src/assets/navbar-icons/campaign-icon-active.svg';
import CampaignDashboardIcon from 'src/assets/navbar-icons/campaign-dashboard-icon.svg';
import CampaignDashboardIconActive from 'src/assets/navbar-icons/campaign-dashboard-icon-active.svg';
import PlaybookIcon from 'src/assets/navbar-icons/playbook-icon.svg';
import PlaybookIconActive from 'src/assets/navbar-icons/playbook-icon-active.svg';
import ChampionsTrackingIcon from 'src/assets/navbar-icons/champions-icon.svg';
import ChampionsTrackingIconActive from 'src/assets/navbar-icons/champions-icon-active.svg';
import WebsiteAccountListIcon from 'src/assets/navbar-icons/wal-icon.svg';
import WebsiteAccountListIconActive from 'src/assets/navbar-icons/wal-icon-active.svg';
import SettingsIcon from 'src/assets/navbar-icons/settings-icon.svg';
import SettingsIconActive from 'src/assets/navbar-icons/settings-icon-active.svg';
import OpportunitiesIcon from 'src/assets/icons/opportunities-nav-icon.svg';
import {
  trackChampionTrackingButtonClick,
  trackRunCMPButtonClick,
  trackSettingsButtonClick,
} from 'src/mix-panel/navbar/function';

export const useNavbarData = (activePath: string) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cmpName = getCookie('cmpName');
  const {
    canAccessDatabaseSearch_module,
    canAccessSettings_module,
    canAccessWebsiteReveal_module,
    canSetupAgents,
  } = useSelector((state: any) => state?.rolePermissions);
  const role = useSelector(userRole);
  const isCmpAccess = useSelector(cmpAccess);
  const featureFlags = useFeatureFlags();
  const adminFeatureFlags = useSelector((state: any) => state.adminFeatureFlags);
  const emailID = useSelector(userEmail);
  const activeTab = location.pathname?.split('/')?.[2];
  const { clientName } = useProfile();

  const checkCmpAccess = () => {
    if (role !== 'DataOps') {
      if (isCmpAccess) {
        return true;
      } else if (!isCmpAccess) {
        return false;
      }
    } else if (role === 'DataOps') {
      return false;
    }
  };

  const navItemEnabled = (itemKey: string) => {
    return (
      featureFlags?.[itemKey] ||
      (adminFeatureFlags?.[itemKey] &&
        (role === 'Admin' || role === 'Superadmin' || role === 'DataOps') &&
        getDomainFromEmail(emailID) === SPROUTS_ADMIN_DOMAIN)
    );
  };

  const navDataV2: NavBarGroupType = {
    'Qualify & Enrich': [
      {
        title: 'Database Search',
        displayName: 'Search',
        isActive: activePath === 'database-search',
        icon: <img src={DBSIcon} alt="dbs-icon" width="16" height="16" />,
        activeIcon: <img src={DBSIconActive} alt="dbs-icon-active" width="16" height="16" />,
        onClick: () => {
          trackDatabaseSearchTabs('accounts', {
            tenant_name: clientName,
          });
          navigate('/database-search/accounts');
        },
        displayCondition: canAccessDatabaseSearch_module,
        beta: false,
        id: 'nav-bar-database-search-menu-button',
        permission: true,
        isLocked: false,
      },
      {
        title: 'Target Profiles',
        displayName: 'Target Profiles',
        isActive:
          activePath === 'target-profiles' &&
          (activeTab === 'contacts' || activeTab === 'accounts'),
        // icon: <TALIcon width="14" height="14" viewBox="0 0 14 14" />,
        // activeIcon: <TALIconActive width="14" height="14" viewBox="0 0 14 14" />,
        icon: <img src={TALIcon} alt="tal-icon" width="16" height="16" />,
        activeIcon: <img src={TALIconActive} alt="tal-icon-active" width="16" height="16" />,
        onClick: () => navigate('/target-profiles/accounts'),
        displayCondition: true,
        id: 'nav-bar-tcl-menu-button',
        permission: true,
        isLocked: false,
      },
      {
        title: 'Lists',
        displayName: 'Lists',
        isActive: activePath === 'target-profiles' && activeTab === 'list',
        // icon: <ListIcon width="12" height="10" viewBox="0 0 12 10" />,
        // activeIcon: <ListIconActive width="12" height="12" viewBox="0 0 12 12" />,
        icon: <img src={ListIcon} alt="list-icon" width="16" height="16" />,
        activeIcon: <img src={ListIconActive} alt="list-icon-active" width="16" height="16" />,
        onClick: () => {
          dispatch(setActiveListTab('contacts'));
          navigate('/target-profiles/list');
          trackContactList(clientName);
        },
        displayCondition: true,
        id: 'nav-bar-list-menu-button',
        permission: true,
        isLocked: false,
      },
    ],
    'Engange & Automate': [
      {
        title: 'Run Campaign',
        displayName: 'Sequence',
        isActive: activePath === 'run-campaign' || activePath === 'run-campaign-rasp',
        // icon: <RunCampaignIcon width="14" height="13" viewBox="0 0 14 13" />,
        // activeIcon: <RunCampaignIconActive width="14" height="13" viewBox="0 0 14 13" />,
        icon: <img src={RunCampaignIcon} alt="run-campaign-icon" width="16" height="16" />,
        activeIcon: <img src={RunCampaignIconActive} alt="run-campaign-icon-active" width="16" height="16" />,
        onClick: () => {
          cmpName === 'PUMPKIN' ? navigate('/run-campaign/') : navigate('/run-campaign-rasp');
          trackRunCMPButtonClick(clientName);
        },
        displayCondition: checkCmpAccess(),
        id: 'nav-bar-run-campaign-dashboard-menu-button',
        permission: true,
        isLocked: false,
      },
      {
        title: 'Campaign Dashboard',
        displayName: 'Campaign Dashboard',
        isActive: activePath === 'dashboard',
        // icon: <CampaignDashboardIcon />,
        // activeIcon: <CampaignDashboardIconActive />,
        icon: <img src={CampaignDashboardIcon} alt="campaign-dashboard-icon" width="16" height="16" />,
        activeIcon: <img src={CampaignDashboardIconActive} alt="campaign-dashboard-icon-active" width="16" height="16" />,
        onClick: () => navigate('/dashboard/campaign'),
        displayCondition: featureFlags.campaignDashboard,
        id: 'nav-bar-campaign-dashboard-menu-button',
        permission: true,
        isLocked: false,
      },
      {
        title: 'AI Workflows',
        displayName: 'AI Workflows',
        isActive: activePath === 'playbook',
        // icon: <PlaybookIcon width="16" height="16" viewBox="0 0 16 16" />,
        // activeIcon: <PlaybookIconActive width="16" height="16" viewBox="0 0 16 16" />,
        icon: <img src={PlaybookIcon} alt="playbook-icon" width="16" height="16" />,
        activeIcon: <img src={PlaybookIconActive} alt="playbook-icon-active" width="16" height="16" />,
        onClick: () => navigate('/playbook'),
        displayCondition:
          featureFlags.automated_playbook &&
          (hasSettingsPermission(canSetupAgents, 'view') ||
            hasSettingsPermission(canSetupAgents, 'edit')),
        id: 'nav-bar-playbook-menu-button',
        permission: true,
        isLocked: false,
      },
    ],
    'Track and Monitor': [
      // {
      //   title: 'Champions Tracking',
      //   displayName: 'Champions Tracking',
      //   isActive: activePath === 'champions-tracking',
      //   // icon: <ChampionsTrackingIcon width="15" height="12" viewBox="0 0 15 12" />,
      //   // activeIcon: <ChampionsTrackingIconActive width="15" height="12" viewBox="0 0 15 12" />,
      //   icon: <img src={ChampionsTrackingIcon} alt="champions-tracking-icon" width="16" height="16" />,
      //   activeIcon: <img src={ChampionsTrackingIconActive} alt="champions-tracking-icon-active" width="16" height="16" />,
      //   onClick: () => {
      //     dispatch(setActiveChampionsTab('allChampions'));
      //     navigate('/champions-tracking/champions', { state: 'champions-tracking' });
      //     trackChampionTrackingButtonClick(clientName);
      //   },
      //   displayCondition: featureFlags?.championTracking,
      //   id: 'nav-bar-champion-tracking-menu-button',
      //   permission: true,
      //   isLocked: false,
      // },
      {
        title: 'Website Account List',
        displayName: 'Website Visits',
        isActive: activePath === 'website',
        // icon: <WebsiteAccountListIcon width="13" height="13" viewBox="0 0 13 13" />,
        // activeIcon: <WebsiteAccountListIconActive width="13" height="13" viewBox="0 0 13 13" />,
        icon: <img src={WebsiteAccountListIcon} alt="website-account-list-icon" width="16" height="16" />,
        activeIcon: <img src={WebsiteAccountListIconActive} alt="website-account-list-icon-active" width="16" height="16" />,
        onClick: () => navigate('/website/visitors/accounts'),
        displayCondition: role !== 'DataOps' && canAccessWebsiteReveal_module,
        id: 'nav-bar-website-account-list-dashboard-menu-button',
        permission: true,
        isLocked: !featureFlags.websiteAccountList,
      },
      {
        title: 'Opportunities',
        displayName: 'Opportunities',
        isActive: activePath === 'opportunities',
        // icon: <OpportunitiesIcon width="16" height="16" viewBox="0 0 24 24" />,
        // activeIcon: <OpportunitiesIcon width="16" height="16" viewBox="0 0 24 24" />,
        icon: <img src={OpportunitiesIcon} alt="opportunities-icon" width="16" height="16" />,
        activeIcon: <img src={OpportunitiesIcon} alt="opportunities-icon-active" width="16" height="16" />,
        onClick: () => navigate('/opportunities'),
        displayCondition: navItemEnabled('showOpportunities'),
        id: 'nav-bar-opportunities-menu-button',
        permission: true,
        isLocked: false,
      },
    ],
    Settings: [
      {
        title: 'Settings',
        displayName: 'Settings',
        isActive: activePath === 'settings',
        // icon: <SettingsIcon width="14" height="14" viewBox="0 0 14 14" />,
        // activeIcon: <SettingsIconActive width="14" height="14" viewBox="0 0 14 14" />,
        icon: <img src={SettingsIcon} alt="settings-icon" width="16" height="16" />,
        activeIcon: <img src={SettingsIconActive} alt="settings-icon-active" width="16" height="16" />,
        onClick: () => {
          navigate('/settings/account');
          trackSettingsButtonClick(clientName);
        },
        displayCondition: role === 'Admin' || role === 'Superadmin' || canAccessSettings_module,
        id: 'nav-bar-settings-dashboard-menu-button',
        permission: true,
        isLocked: false,
      },
    ],
  };

  return { navDataV2 };
};
